// src/components/LegalModal.js
//
// Shared Terms of Service / Privacy Policy modal. Fetches the real source
// documents at runtime and renders them — the .md files are the single
// source of truth. To update the legal text, edit the .md file only;
// nothing in this component needs to change.
//
// Expects the following to be served as static assets (e.g. dropped into
// Create React App's public/ folder, so they're available at these paths
// on the deployed site):
//   /legal/terms-of-service.md
//   /legal/privacy-policy.md
//
// Usage:
//   const [legalModal, setLegalModal] = useState(null); // "terms" | "privacy" | null
//   <span onClick={() => setLegalModal("terms")}>Terms of Service</span>
//   <span onClick={() => setLegalModal("privacy")}>Privacy Policy</span>
//   <LegalModal type={legalModal} onClose={() => setLegalModal(null)} />

import { useState, useEffect } from "react";

const SOURCES = {
  terms: "/legal/terms-of-service.md",
  privacy: "/legal/privacy-policy.md",
};

// Module-level cache so re-opening the modal within the same page load
// doesn't re-fetch the file every time.
const cache = {};

// Renders **bold** spans within a line; everything else stays plain text.
function parseInline(text, keyPrefix) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={`${keyPrefix}-b${i}`}>{part.slice(2, -2)}</strong>
      : <span key={`${keyPrefix}-t${i}`}>{part}</span>
  );
}

// Minimal markdown -> JSX conversion. Only covers what our own legal docs
// actually use: # / ## headings, **bold**, --- rules, and "- " lists.
// Not a general-purpose markdown renderer — if the .md files start using
// other syntax (tables, links, nested lists, etc.), this will need extending.
function parseMarkdown(md) {
  let lines = md.split("\n");

  // Drop a leading "# Title" line — the modal's header bar already shows
  // the title, so rendering it again in the body would be redundant.
  const firstContentIdx = lines.findIndex((l) => l.trim() !== "");
  if (firstContentIdx !== -1 && lines[firstContentIdx].trim().startsWith("# ")) {
    lines = [...lines.slice(0, firstContentIdx), ...lines.slice(firstContentIdx + 1)];
  }

  const nodes = [];
  let listBuffer = [];

  const flushList = (key) => {
    if (!listBuffer.length) return;
    nodes.push(
      <ul key={`ul-${key}`} style={{ margin: "8px 0 12px 20px", padding: 0 }}>
        {listBuffer.map((item, i) => <li key={i} style={{ marginBottom: 4 }}>{parseInline(item, `li-${key}-${i}`)}</li>)}
      </ul>
    );
    listBuffer = [];
  };

  lines.forEach((rawLine, idx) => {
    const line = rawLine.trim();

    if (line === "") {
      flushList(idx);
      return;
    }
    if (line === "---") {
      flushList(idx);
      nodes.push(<hr key={`hr-${idx}`} style={{ border: "none", borderTop: "1px solid #e2eaf3", margin: "16px 0" }} />);
      return;
    }
    if (line.startsWith("## ")) {
      flushList(idx);
      nodes.push(
        <div key={`h2-${idx}`} style={{ fontWeight: 700, fontSize: 14, color: "#0f2944", marginTop: 18, marginBottom: 8 }}>
          {parseInline(line.slice(3), `h2-${idx}`)}
        </div>
      );
      return;
    }
    if (line.startsWith("# ")) {
      flushList(idx);
      nodes.push(
        <div key={`h1-${idx}`} style={{ fontWeight: 700, fontSize: 16, color: "#0f2944", marginTop: 4, marginBottom: 8 }}>
          {parseInline(line.slice(2), `h1-${idx}`)}
        </div>
      );
      return;
    }
    if (line.startsWith("- ")) {
      listBuffer.push(line.slice(2));
      return;
    }
    flushList(idx);
    nodes.push(
      <div key={`p-${idx}`} style={{ marginBottom: 10 }}>
        {parseInline(line, `p-${idx}`)}
      </div>
    );
  });

  flushList("end");
  return nodes;
}

export default function LegalModal({ type, onClose }) {
  const [content, setContent] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!type) return;

    if (cache[type]) {
      setContent(cache[type]);
      return;
    }

    setContent(null);
    setError(false);

    fetch(SOURCES[type])
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load ${type} (${res.status})`);
        return res.text();
      })
      .then((text) => {
        cache[type] = text;
        setContent(text);
      })
      .catch(() => setError(true));
  }, [type]);

  if (!type) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white", borderRadius: 16,
          width: "100%", maxWidth: 540,
          maxHeight: "80vh", display: "flex", flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "16px 20px", borderBottom: "1px solid #e2eaf3",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: "#0f2944" }}>
            {type === "terms" ? "Terms of Service" : "Privacy Policy"}
          </span>
          <button onClick={onClose} style={{
            width: 30, height: 30, border: "1px solid #e2eaf3", borderRadius: 8,
            background: "none", cursor: "pointer", fontSize: 13, color: "#4a6080",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>✕</button>
        </div>

        {/* Scrollable content */}
        <div style={{ overflowY: "auto", padding: "20px 24px", flex: 1, fontSize: 13, color: "#4a6080", lineHeight: 1.8 }}>
          {error && (
            <div style={{ textAlign: "center", color: "#c0392b" }}>
              Couldn't load this document.{" "}
              <a href={SOURCES[type]} target="_blank" rel="noreferrer" style={{ color: "#1a6fa8" }}>
                Open it directly
              </a>.
            </div>
          )}
          {!error && !content && (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <i className="pi pi-spin pi-spinner" style={{ fontSize: 20, color: "#1a6fa8" }} />
            </div>
          )}
          {!error && content && parseMarkdown(content)}
        </div>

        <div style={{ padding: "12px 20px", borderTop: "1px solid #e2eaf3", flexShrink: 0, textAlign: "center" }}>
          <button onClick={onClose} style={{
            padding: "9px 28px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg, #1a6fa8, #0d9488)",
            color: "white", fontWeight: 600, fontSize: 13,
            fontFamily: "'DM Sans', sans-serif", cursor: "pointer",
          }}>Close</button>
        </div>
      </div>
    </div>
  );
}
